import {
	type TenantMetadata,
	ThemeButtonVariant,
	ThemeRadiusScale,
	type ToolbarButtonRadius
} from '../types/tenant-metadata.types.ts';

const TOOLBAR_BUTTON_VARIANTS: readonly string[] =
	Object.values(ThemeButtonVariant);
const TOOLBAR_BUTTON_RADII: readonly ToolbarButtonRadius[] = [
	ThemeRadiusScale.Sm,
	ThemeRadiusScale.Md,
	ThemeRadiusScale.Lg,
	ThemeRadiusScale.Xl
];

const REQUIRED_METADATA_KEYS = [
	'id',
	'name',
	'branding',
	'pages',
	'integrations',
	'feature_flags',
	'featureBadges',
	'creator_settings',
	'portal_settings',
	'legal_links'
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOneOf(value: unknown, allowed: readonly string[]): boolean {
	return (
		value === undefined || value === null || allowed.includes(value as string)
	);
}

export function assertTenantMetadata(
	value: unknown,
	partnerIdn: string,
	source: string
): asserts value is TenantMetadata {
	if (!isRecord(value)) {
		throw new Error(
			`Failed to load tenant metadata for "${partnerIdn}": ${source} is not an object.`
		);
	}

	for (const key of REQUIRED_METADATA_KEYS) {
		if (!(key in value)) {
			throw new Error(
				`Failed to load tenant metadata for "${partnerIdn}": ${source} is missing required field "${key}".`
			);
		}
	}

	if (!isRecord(value.pages)) {
		throw new Error(
			`Failed to load tenant metadata for "${partnerIdn}": ${source} field "pages" must be an object.`
		);
	}

	if (!isRecord(value.portal_settings)) {
		throw new Error(
			`Failed to load tenant metadata for "${partnerIdn}": ${source} field "portal_settings" must be an object.`
		);
	}

	if (
		isRecord(value.theme) &&
		isRecord(value.theme.ui) &&
		isRecord(value.theme.ui.toolbarButton)
	) {
		const { radius, variant } = value.theme.ui.toolbarButton;

		if (!isOneOf(variant, TOOLBAR_BUTTON_VARIANTS)) {
			throw new Error(
				`Failed to load tenant metadata for "${partnerIdn}": ${source} field "theme.ui.toolbarButton.variant" has unsupported value "${String(variant)}".`
			);
		}

		if (!isOneOf(radius, TOOLBAR_BUTTON_RADII)) {
			throw new Error(
				`Failed to load tenant metadata for "${partnerIdn}": ${source} field "theme.ui.toolbarButton.radius" has unsupported value "${String(radius)}".`
			);
		}
	}
}
